import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Distribute from './pages/Distribute';
import Pools from './pages/Pools';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import AdminPortal from './pages/AdminPortal';
import LedgerHistory from './pages/LedgerHistory';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Stats from './pages/Stats';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Token from './pages/Token';
import Logo from './components/Logo';
import ErrorBoundary from './components/ErrorBoundary';
import ApiErrorBanner from './components/ApiErrorBanner';
import { useAuth } from './contexts/AuthContext';
import api from './api';

// --- SECURITY WRAPPER ---
// Redirects to /login if not authenticated
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
            </div>
        );
    }

    if (!isAuthenticated) return <Navigate to="/login" replace />;
    return children;
};

// --- ADMIN ROUTE ---
// Requires login AND admin role; shows 403 otherwise
const AdminRoute = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();

    if (isLoading) {
        return (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
            </div>
        );
    }

    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (user?.role !== 'admin') {
        return (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <h2>403 — Access Denied</h2>
                <p style={{ color: 'var(--text-muted)' }}>You don't have permission to view this page.</p>
                <Link to="/dashboard" className="nav-link">← Back to Dashboard</Link>
            </div>
        );
    }
    return children;
};

// --- LOGOUT BUTTON COMPONENT ---
const LogoutButton = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <button onClick={handleLogout} className="nav-link nav-pill">
            Logout
        </button>
    );
};

// --- WITHDRAW MODAL ---
const WithdrawModal = ({ onClose, user }) => {
    const [kycLoading, setKycLoading] = useState(false);
    const [kycError, setKycError] = useState('');
    const [walletBalance, setWalletBalance] = useState(null);
    const [showUsdcWithdraw, setShowUsdcWithdraw] = useState(false);
    const [usdcWithdrawAmount, setUsdcWithdrawAmount] = useState(0);
    const [usdcWithdrawDest, setUsdcWithdrawDest] = useState('');
    const [usdcWithdrawLoading, setUsdcWithdrawLoading] = useState(false);
    const [usdcWithdrawError, setUsdcWithdrawError] = useState('');
    const [usdcWithdrawTx, setUsdcWithdrawTx] = useState('');

    const WITHDRAW_MIN_USD = 1;

    useEffect(() => {
        api.get('/balance').then(r => setWalletBalance(r.data.balance ?? 0)).catch(() => setWalletBalance(0));
    }, []);

    const handleStartKyc = async () => {
        setKycLoading(true);
        setKycError('');
        try {
            const res = await api.post('/kyc/session');
            if (!res.data.url) {
                setKycError('Verification started but no redirect URL returned.');
                return;
            }
            window.location.href = res.data.url;
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to start verification. Please try again.';
            const detail = err.response?.data?.detail;
            setKycError(detail ? `${msg} (${detail})` : msg);
        } finally {
            setKycLoading(false);
        }
    };

    const handleUsdcWithdraw = async (e) => {
        e.preventDefault();
        setUsdcWithdrawError('');
        setUsdcWithdrawLoading(true);
        try {
            const res = await api.post('/wallet/withdraw-usdc', { amount_usdc: usdcWithdrawAmount, destination: usdcWithdrawDest.trim() });
            setUsdcWithdrawTx(res.data.txHash);
        } catch (err) {
            setUsdcWithdrawError(err.response?.data?.error || 'Withdrawal failed. Please try again.');
        } finally {
            setUsdcWithdrawLoading(false);
        }
    };

    return (
    <div
        style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
            backdropFilter: 'blur(4px)',
        }}
        onClick={onClose}
    >
        <div
            style={{
                background: 'linear-gradient(135deg, rgba(26,26,46,0.98) 0%, rgba(18,18,26,0.98) 100%)',
                border: '1px solid rgba(255,193,7,0.2)',
                borderRadius: '24px',
                padding: '40px',
                maxWidth: '460px',
                width: '100%',
                boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
                backdropFilter: 'blur(20px)',
                position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
        >
            {/* Close */}
            <button
                onClick={onClose}
                style={{
                    position: 'absolute', top: '16px', right: '16px',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.6)', borderRadius: '8px',
                    width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >✕</button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <h2 style={{ margin: 0, color: '#ffc107', fontSize: '1.5rem', fontWeight: 700 }}>
                    Withdraw Funds
                </h2>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                    {user?.kyc_status === 'Approved' && (
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)',
                            borderRadius: '20px', padding: '4px 14px',
                            fontSize: '0.75rem', fontWeight: 700, color: '#22c55e',
                        }}>
                            ✔ Identity Verified
                        </div>
                    )}
                </div>
            </div>

            {/* Message */}
            <p style={{
                color: 'rgba(255,255,255,0.72)', textAlign: 'center',
                lineHeight: 1.75, marginBottom: '16px', fontSize: '0.95rem',
            }}>
                {user?.kyc_status !== 'Approved' && 'Withdrawals will be available after completion of identity verification. '}
                Send USDC to any Solana wallet, or spend it with your Rain Visa Card.
            </p>
            {user?.kyc_status !== 'Approved' && (
                <div style={{
                    background: 'rgba(255,193,7,0.08)',
                    border: '1px solid rgba(255,193,7,0.3)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '32px',
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    color: 'rgba(255,193,7,0.85)',
                }}>
                    Withdrawals are currently only available for <strong>US residents</strong>.
                </div>
            )}

            {/* KYC button for unverified users */}
            {user?.kyc_status !== 'Approved' && (
                <div style={{ marginBottom: '20px' }}>
                    <button
                        onClick={handleStartKyc}
                        disabled={kycLoading}
                        style={{
                            width: '100%', padding: '12px 24px', borderRadius: '14px', border: 'none',
                            background: kycLoading ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                            color: kycLoading ? 'rgba(255,255,255,0.3)' : '#000',
                            fontWeight: 700, fontSize: '0.95rem',
                            cursor: kycLoading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {kycLoading ? 'Processing…' : 'Complete KYC Verification'}
                    </button>
                    {kycError && (
                        <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '8px', textAlign: 'center' }}>{kycError}</p>
                    )}
                </div>
            )}

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(() => {
                    // walletBalance is USDC (= USD)
                    const available = walletBalance !== null ? Math.max(0, walletBalance) : null;
                    const hasEnough = available !== null && available >= WITHDRAW_MIN_USD;

                    if (available === null) return (
                        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', padding: '14px' }}>
                            Loading balance…
                        </div>
                    );

                    if (!hasEnough) return (
                        <div style={{
                            padding: '14px 16px', borderRadius: '14px',
                            border: '1px solid rgba(153,69,255,0.2)',
                            background: 'rgba(153,69,255,0.05)',
                            color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', textAlign: 'center',
                        }}>
                            ◎ Send USDC to Solana Wallet
                            <div style={{ fontSize: '0.75rem', marginTop: '6px', color: 'rgba(255,255,255,0.3)' }}>
                                Minimum ${WITHDRAW_MIN_USD} USDC required
                            </div>
                            <div style={{ fontSize: '0.75rem', marginTop: '4px', color: 'rgba(255,255,255,0.2)' }}>
                                Available balance: ${available.toFixed(2)} USDC
                            </div>
                        </div>
                    );

                    if (usdcWithdrawTx) return (
                        <div style={{
                            padding: '14px 16px', borderRadius: '14px',
                            border: '1px solid rgba(153,69,255,0.35)',
                            background: 'rgba(153,69,255,0.08)',
                            color: '#a78bfa', fontSize: '0.85rem', textAlign: 'center',
                        }}>
                            ✅ Sent successfully!
                            <div style={{ fontSize: '0.72rem', marginTop: '6px', color: 'rgba(255,255,255,0.3)' }}>
                                <a href={`https://solscan.io/tx/${usdcWithdrawTx}`} target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>
                                    View on Solscan
                                </a>
                            </div>
                        </div>
                    );

                    if (showUsdcWithdraw) {
                        const feeUsdc = parseFloat((usdcWithdrawAmount * 0.05).toFixed(2));
                        const youReceive = parseFloat((usdcWithdrawAmount * 0.95).toFixed(2));
                        return (
                            <form onSubmit={handleUsdcWithdraw} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)' }}>
                                        Destination Solana address
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. 9QhMt2w8..."
                                        value={usdcWithdrawDest}
                                        onChange={e => setUsdcWithdrawDest(e.target.value)}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: '1px solid rgba(153,69,255,0.35)',
                                            background: 'rgba(153,69,255,0.06)',
                                            color: '#fff', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box',
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                                    <span>Amount</span>
                                    <span style={{ color: '#a78bfa', fontWeight: 700 }}>
                                        ${usdcWithdrawAmount.toFixed(2)} USDC
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={WITHDRAW_MIN_USD}
                                    max={available.toFixed(2)}
                                    step={0.01}
                                    value={usdcWithdrawAmount}
                                    onChange={e => setUsdcWithdrawAmount(parseFloat(e.target.value))}
                                    style={{ width: '100%', accentColor: '#a78bfa', cursor: 'pointer' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>
                                    <span>Min: ${WITHDRAW_MIN_USD}</span>
                                    <span>Max: ${available.toFixed(2)}</span>
                                </div>
                                <div style={{
                                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: '10px', padding: '10px 14px',
                                    display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.82rem',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.5)' }}>
                                        <span>You Bee fee (5%)</span>
                                        <span style={{ color: '#ffc107' }}>−${feeUsdc.toFixed(2)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#a78bfa', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '6px' }}>
                                        <span>Recipient receives</span>
                                        <span>${youReceive.toFixed(2)} USDC</span>
                                    </div>
                                </div>
                                {usdcWithdrawError && <p style={{ margin: 0, color: '#ef4444', fontSize: '0.82rem' }}>{usdcWithdrawError}</p>}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button type="button" onClick={() => setShowUsdcWithdraw(false)} style={{
                                        flex: 1, padding: '12px', borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        background: 'transparent', color: 'rgba(255,255,255,0.6)',
                                        fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
                                    }}>Cancel</button>
                                    <button type="submit" disabled={usdcWithdrawLoading || usdcWithdrawAmount < WITHDRAW_MIN_USD || !usdcWithdrawDest.trim()} style={{
                                        flex: 2, padding: '12px', borderRadius: '12px', border: 'none',
                                        background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
                                        color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                                        cursor: (usdcWithdrawLoading || usdcWithdrawAmount < WITHDRAW_MIN_USD || !usdcWithdrawDest.trim()) ? 'not-allowed' : 'pointer',
                                        opacity: (usdcWithdrawLoading || usdcWithdrawAmount < WITHDRAW_MIN_USD || !usdcWithdrawDest.trim()) ? 0.6 : 1,
                                    }}>
                                        {usdcWithdrawLoading ? 'Sending…' : 'Send USDC →'}
                                    </button>
                                </div>
                            </form>
                        );
                    }

                    return (
                        <button onClick={() => { setShowUsdcWithdraw(true); setUsdcWithdrawAmount(WITHDRAW_MIN_USD); }} style={{
                            padding: '14px 24px', borderRadius: '14px',
                            border: '1px solid rgba(153,69,255,0.45)',
                            background: 'rgba(153,69,255,0.1)',
                            color: '#a78bfa', fontWeight: 700, fontSize: '0.95rem',
                            cursor: 'pointer', width: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                        }}>
                            ◎ Send USDC to Solana Wallet
                        </button>
                    );
                })()}
            </div>

            {/* Rain Card link */}
            {user?.kyc_status === 'Approved' && (() => {
                const hasEnough = walletBalance !== null && walletBalance >= 20;
                if (hasEnough) return (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                        <Link
                            to="/profile#rain-card"
                            onClick={onClose}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                padding: '10px 22px', borderRadius: '12px',
                                border: '1px solid rgba(99,91,255,0.4)',
                                background: 'rgba(99,91,255,0.08)',
                                color: '#a78bfa', fontWeight: 600, fontSize: '0.88rem',
                                textDecoration: 'none', transition: 'all 0.2s',
                            }}
                        >
                            💳 Get a Rain Visa Card
                            <span style={{ fontSize: '0.72rem', color: 'rgba(167,139,250,0.6)' }}>spend your distributions →</span>
                        </Link>
                    </div>
                );
                return (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            padding: '10px 22px', borderRadius: '12px',
                            border: '1px solid rgba(99,91,255,0.2)',
                            background: 'rgba(99,91,255,0.04)',
                            color: 'rgba(167,139,250,0.4)', fontWeight: 600, fontSize: '0.88rem',
                            cursor: 'not-allowed',
                        }}>
                            💳 Get a Rain Visa Card
                            <span style={{ fontSize: '0.72rem', color: 'rgba(167,139,250,0.3)' }}>$20 min balance required</span>
                        </div>
                    </div>
                );
            })()}

            {/* Tax disclaimer */}
            <p style={{
                marginTop: '16px', fontSize: '0.78rem',
                color: 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.5,
            }}>
                Payments may be considered taxable income. Consult a tax advisor or report as "Other Income" on your federal return.
            </p>
        </div>
    </div>
    );
};

// --- NAVIGATION BAR ---
const NavBar = () => {
    const location = useLocation();
    const { user } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [withdrawOpen, setWithdrawOpen] = useState(false);

    // Hide navbar on public pages (landing, login, register); show on /stats only if logged in
    if (['/', '/login', '/register', '/privacy', '/terms', '/forgot-password', '/reset-password'].includes(location.pathname)) return null;
    if (location.pathname === '/stats' && !user) return null;

    const isAdmin = user?.role === 'admin';
    const close = () => setMenuOpen(false);

    return (
        <div>
            <nav className="nav-bar" style={{
                padding: '15px 30px',
                background: '#212529',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <Link to="/dashboard" style={{ textDecoration: 'none' }} onClick={close}>
                        <Logo />
                    </Link>
                    <div className="nav-desktop-links" style={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
                        <NavLink to="/dashboard" className={({ isActive }) => 'nav-link nav-pill' + (isActive ? ' nav-pill-active' : '')}>Dashboard</NavLink>
                        <NavLink to="/pools" className={({ isActive }) => 'nav-link nav-pill' + (isActive ? ' nav-pill-active' : '')}>Hives</NavLink>
                        <button className="nav-link nav-pill" onClick={() => setWithdrawOpen(true)}>Withdraw</button>
                        <NavLink to="/stats" className={({ isActive }) => 'nav-link nav-pill' + (isActive ? ' nav-pill-active' : '')}>Community Stats</NavLink>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div className="nav-right-links" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <NavLink to="/profile" className={({ isActive }) => 'nav-link nav-pill' + (isActive ? ' nav-pill-active' : '')}>My Profile</NavLink>
                        {isAdmin && (<>
                            <NavLink to="/ledger" className={({ isActive }) => 'nav-link nav-pill nav-pill-admin' + (isActive ? ' nav-pill-active' : '')}>Ledger</NavLink>
                            <NavLink to="/youbeehive" className={({ isActive }) => 'nav-link nav-pill nav-pill-admin' + (isActive ? ' nav-pill-active' : '')}>Hive Admin</NavLink>
                        </>)}
                        <LogoutButton />
                    </div>
                    <button
                        className="nav-hamburger"
                        onClick={() => setMenuOpen(o => !o)}
                        aria-label="Toggle menu"
                    >
                        {menuOpen ? '✕' : '☰'}
                    </button>
                </div>
            </nav>

            {/* Mobile drawer */}
            <div className={`nav-drawer${menuOpen ? ' open' : ''}`}>
                <Link to="/dashboard" className="nav-link" onClick={close}>Dashboard</Link>
                <Link to="/pools" className="nav-link" onClick={close}>Hives</Link>
                <button className="nav-link" onClick={() => { setWithdrawOpen(true); close(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>Withdraw</button>
                <Link to="/profile" className="nav-link" onClick={close}>My Profile</Link>
                <Link to="/stats" className="nav-link" onClick={close}>Community Stats</Link>
                {isAdmin && (<>
                    <Link to="/ledger" className="nav-link" onClick={close} style={{ color: '#ffc107' }}>Ledger</Link>
                    <Link to="/youbeehive" className="nav-link" onClick={close} style={{ color: '#ffc107' }}>Hive Admin</Link>
                </>)}
                <div style={{ marginTop: '4px' }}>
                    <LogoutButton />
                </div>
            </div>

            {withdrawOpen && <WithdrawModal onClose={() => setWithdrawOpen(false)} user={user} />}
        </div>
    );
};

function App() {
  // Handle session expired events from the API interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      window.location.href = '/login';
    };

    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('auth:sessionExpired', handleSessionExpired);
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <div className="App" style={{ fontFamily: 'Arial, sans-serif', minHeight: '100vh' }}>

          <ApiErrorBanner />
          <NavBar />

          <div style={{ padding: '0' }}>
              <Routes>
                  {/* PUBLIC */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/stats" element={<Stats />} />
                  <Route path="/token" element={<Token />} />

                  {/* PROTECTED */}
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/distribute" element={<ProtectedRoute><Distribute /></ProtectedRoute>} />
                  <Route path="/pools" element={<ProtectedRoute><Pools /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                  {/* ADMIN ONLY */}
                  <Route path="/ledger" element={<AdminRoute><LedgerHistory /></AdminRoute>} />
                  <Route path="/youbeehive" element={<AdminRoute><AdminPortal /></AdminRoute>} />

                  {/* 404 */}
                  <Route path="*" element={
                      <div style={{
                          minHeight: '80vh',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '60px 20px',
                          textAlign: 'center',
                          background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
                      }}>
                          <h2 style={{ color: '#ffffff', fontSize: '2rem', marginBottom: '12px' }}>404</h2>
                          <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '24px' }}>The page you're looking for doesn't exist.</p>
                          <Link to="/" style={{
                              padding: '12px 24px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                              color: '#000',
                              fontWeight: 700,
                              textDecoration: 'none',
                          }}>Go Home</Link>
                      </div>
                  } />
              </Routes>
          </div>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
